import { useState, useEffect } from "react";
import { toast } from 'sonner';
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock, AlertCircle, CheckCircle } from "lucide-react";
import meetingService from "../services/meetingService";
import { MEETING_TYPES } from "../utils/financialTaxonomy";

export interface ProfessionalProfile {
  id: string;
  name: string;
  avatar?: string;
  // Add other fields as needed
}

interface ScheduleMeetingDialogProps {
  professional: ProfessionalProfile | null;
  requestId?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onScheduleSuccess?: () => void;
}

export function ScheduleMeetingDialog({ professional, requestId, isOpen, onOpenChange, onScheduleSuccess }: ScheduleMeetingDialogProps) {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [time, setTime] = useState<string>("09:00");
  const [duration, setDuration] = useState<string>("30");
  const [meetingTitle, setMeetingTitle] = useState<string>("");
  const [meetingDescription, setMeetingDescription] = useState<string>("");
  const [meetingType, setMeetingType] = useState<string>("intro");
  const [locationType, setLocationType] = useState<string>("virtual");
  const [locationAddress, setLocationAddress] = useState<string>("");
  const [videoLink, setVideoLink] = useState<string>("");
  const [additionalEmails, setAdditionalEmails] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  // Check availability whenever date, time, or duration changes
  useEffect(() => {
    const checkSlotAvailability = async () => {
      if (!professional || !date || !isOpen) return;

      try {
        setIsCheckingAvailability(true);
        setIsAvailable(null);
        
        const userId = localStorage.getItem("userId");
        const startTime = new Date(date);
        const [hours, minutes] = time.split(":").map(Number);
        startTime.setHours(hours, minutes, 0, 0);
        
        const durationMins = parseInt(duration);
        const endTime = new Date(startTime.getTime() + durationMins * 60000);

        const response = await meetingService.checkAvailability({
          hostId: userId,
          participantId: professional.id,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString()
        });

        const data = response.data || response;
        setIsAvailable(data.available);
      } catch (error) {
        console.error("Error checking availability:", error);
        setIsAvailable(true);
      } finally {
        setIsCheckingAvailability(false);
      }
    };

    const timer = setTimeout(() => {
      checkSlotAvailability();
    }, 500);

    return () => clearTimeout(timer);
  }, [date, time, duration, professional, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsAvailable(null);
      setIsCheckingAvailability(false);
    }
  }, [isOpen]);

  const handleSchedule = async () => {
    if (!meetingTitle.trim()) {
      toast.error("Please provide a meeting title");
      return;
    }

    if (!date) {
      toast.error("Please select a date");
      return;
    }

    if ((locationType === "in_person" || locationType === "hybrid") && !locationAddress.trim()) {
      toast.error("Please provide an address for the in-person meeting");
      return;
    }

    if ((locationType === "virtual" || locationType === "hybrid") && !videoLink.trim()) {
      toast.error("Please provide a video call link");
      return;
    }

    try {
      setIsSubmitting(true);
      const userId = localStorage.getItem("userId");
      
      const startTime = new Date(date);
      const [hours, minutes] = time.split(":").map(Number);
      startTime.setHours(hours, minutes, 0, 0);
      
      const durationMins = parseInt(duration);
      const endTime = new Date(startTime.getTime() + durationMins * 60000);

      await meetingService.scheduleMeeting({
        hostId: userId,
        participantId: professional?.id,
        researchAdvisingRequestId: requestId, // Changed to request_id
        title: meetingTitle,
        description: meetingDescription,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        durationInMins: durationMins,
        meetingType: meetingType.toUpperCase(),
        locationType: locationType.toUpperCase(),
        locationAddress: (locationType === "in_person" || locationType === "hybrid") ? locationAddress : null,
        videoCallLink: (locationType === "virtual" || locationType === "hybrid") ? videoLink : null,
        additionalParticipantEmails: additionalEmails.split(",").map(e => e.trim()).filter(e => e !== ""),
        status: "PENDING"
      });

      toast.success(`Meeting scheduled with ${professional?.name}`);
      onOpenChange(false);
      if (onScheduleSuccess) onScheduleSuccess();
      
      setMeetingTitle("");
      setMeetingDescription("");
      setLocationAddress("");
      setVideoLink("");
      setAdditionalEmails("");
    } catch (error) {
      console.error("Error scheduling meeting:", error);
      toast.error("Failed to schedule meeting. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"
  ];

  const durationOptions = [
    { label: "15 Minutes", value: "15" },
    { label: "30 Minutes", value: "30" },
    { label: "45 Minutes", value: "45" },
    { label: "1 Hour", value: "60" },
    { label: "1.5 Hour", value: "90" },
    { label: "2 Hour", value: "120" },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule Meeting</DialogTitle>
          <DialogDescription>
            Book a session with {professional?.name}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="title">Meeting Title</Label>
            <Input
              id="title"
              placeholder="e.g., Initial Portfolio Consultation"
              value={meetingTitle}
              onChange={(e) => setMeetingTitle(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Briefly describe the purpose of this meeting..."
              value={meetingDescription}
              onChange={(e) => setMeetingDescription(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={`w-full justify-start text-left font-normal ${!date && "text-muted-foreground"}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "MMM d, yyyy") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}  
                    disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-2">
                <Label>Time Slot</Label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger>
                    <SelectValue placeholder="Time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Duration</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger>
                    <SelectValue placeholder="Duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {durationOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label>Meeting Type</Label>
              <Select value={meetingType} onValueChange={setMeetingType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEETING_TYPES.map((mt) => (
                    <SelectItem key={mt.value} value={mt.value}>
                      {mt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label>Location</Label>
              <Select value={locationType} onValueChange={setLocationType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="virtual">Virtual (Video Call)</SelectItem>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="phone">Phone Call</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {(locationType === "in_person" || locationType === "hybrid") && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="address">Meeting Address</Label>
              <Input
                id="address"
                placeholder="Enter office address or location..."
                value={locationAddress}
                onChange={(e) => setLocationAddress(e.target.value)}
              />
            </div>
          )}

          {(locationType === "virtual" || locationType === "hybrid") && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="video-link">Video Call Link</Label>
              <Input
                id="video-link"
                placeholder="Zoom, Google Meet, or Teams link..."
                value={videoLink}
                onChange={(e) => setVideoLink(e.target.value)}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="emails">Additional Participant Emails</Label>
            <Input
              id="emails"
              placeholder="email1@example.com, email2@example.com"
              value={additionalEmails}
              onChange={(e) => setAdditionalEmails(e.target.value)}
            />
            <p className="text-[10px] text-muted-foreground">Separate multiple emails with commas</p>
          </div>

          {/* Availability Status */}
          <div className="py-2">
            {isCheckingAvailability ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground animate-pulse">
                <Clock className="h-3 w-3 animate-spin" />
                Checking availability...
              </div>
            ) : isAvailable === false ? (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded-md">
                <AlertCircle className="h-3 w-3" />
                This time slot is already booked. Please select another time.
              </div>
            ) : isAvailable === true ? (
              <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 p-2 rounded-md">
                <CheckCircle className="h-3 w-3" />
                Slot is available!
              </div>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSchedule} disabled={isSubmitting || isCheckingAvailability || isAvailable === false}>
            {isSubmitting ? "Scheduling..." : "Confirm Meeting"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
